import type { PixelCrop } from 'react-image-crop';

/**
 * Draws the cropped region of an image onto a canvas.
 * Based on react-image-crop demo: https://github.com/DominicTobias/react-image-crop
 */
export function getCroppedCanvas(
  image: HTMLImageElement,
  crop: PixelCrop,
  scale = 1,
  rotate = 0,
  shape: 'rect' | 'round' = 'rect'
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const pixelRatio = window.devicePixelRatio;

  canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
  canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

  ctx.scale(pixelRatio, pixelRatio);
  ctx.imageSmoothingQuality = 'high';

  // Fill with white so transparent PNG areas and padding render as white (not black)
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const cropX = crop.x * scaleX;
  const cropY = crop.y * scaleY;
  const rotateRads = (rotate * Math.PI) / 180;
  const centerX = image.naturalWidth / 2;
  const centerY = image.naturalHeight / 2;

  // For round shape, clip to a circle — white corners remain from the fill above
  if (shape === 'round') {
    const cx = (crop.width * scaleX) / 2;
    const cy = (crop.height * scaleY) / 2;
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, Math.min(cx, cy), 0, Math.PI * 2);
    ctx.clip();
  }

  ctx.save();
  ctx.translate(-cropX, -cropY);
  ctx.translate(centerX, centerY);
  ctx.rotate(rotateRads);
  ctx.scale(scale, scale);
  ctx.translate(-centerX, -centerY);
  ctx.drawImage(
    image,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight
  );
  ctx.restore();

  if (shape === 'round') {
    ctx.restore();
  }

  return canvas;
}

export function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType = 'image/jpeg',
  quality = 0.92
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob from canvas'));
        }
      },
      mimeType,
      quality
    );
  });
}
